/**
 * Tilläggsavtal – Hyresgästanpassning · Version 2026
 * (tillaggsavtal_hyresgastanpassning v1).
 *
 * A connected child of a lokalhyresavtal with its own signing. Three
 * mutually exclusive alternatives (A1 investeringsbidrag, A2 hyresvärden
 * genomför, A3 hyresgästen genomför) — only the chosen one prints; the
 * sub-choices inside an alternative print every option with a box.
 * Text verbatim from `Tillaggsavtal_Hyresgastanpassning.docx`.
 */
import { option, optionList, p, section, t, table } from '@hyranai/regta-document-ir';
import { annexOrBlank, attachmentListNodes, contextAttachments, customSectionsBlockDefinition, optionChosen, partySlot, pick, propertySlot, selectSlot, shown, signingBlockDefinition, slotText, textVal, ticked, whenOption, } from '../common/index.js';
import { ALTERNATIVE_OPTIONS, KOMPENSATION_OPTIONS, UTBETALNING_OPTIONS, alternativeA1, alternativeA2, alternativeA3, } from './alternatives.js';
export { ALTERNATIVE_OPTIONS, KOMPENSATION_OPTIONS, UTBETALNING_OPTIONS };
const K = 'tillaggsavtal_hyresgastanpassning';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'Tillaggsavtal_Hyresgastanpassning.docx' },
};
const FROM_PARENT = 'Förifylls från lokalhyresavtalet.';
export const avtalskoppling = {
    ...LEGAL,
    plane: 'shared',
    blockKey: `${K}.avtalskoppling`,
    version: 'v1',
    title: 'Avtalskoppling',
    summary: 'Identifies the supplement, the lokalhyresavtal it amends, the premises and both parties.',
    slots: [
        { key: 'ta_number', label: 'Tilläggsavtal nr', type: 'text', required: true, help: 'Egen referens, t.ex. TA-2042.' },
        {
            key: 'ta_date',
            label: 'Datum för tilläggsavtal',
            type: 'date',
            help: 'Fyll i endast om tilläggsavtalet ingås senare än vid avtalsslut.',
        },
        {
            key: 'parent_contract_number',
            label: 'Hyresavtal nr',
            type: 'text',
            required: true,
            derivedFrom: 'contract_number',
            help: 'Förifylls från lokalhyresavtalets avtalsnummer.',
        },
        propertySlot('property_designation', 'Fastighet', 'designation', { required: true, help: 'Förifylls från lokalhyresavtalets fastighet.' }),
        propertySlot('property_address', 'Lokalens adress', 'address', {
            type: 'textarea',
            required: true,
            help: 'Förifylls från lokalhyresavtalets fastighet.',
        }),
        partySlot('landlord_name', 'Hyresvärd – namn', 'lessor', 0, 'name', { required: true, help: FROM_PARENT }),
        partySlot('landlord_org_number', 'Hyresvärd – org.nr', 'lessor', 0, 'legalId', { required: true, help: FROM_PARENT }),
        partySlot('tenant_name', 'Hyresgäst – namn', 'lessee', 0, 'name', { required: true, help: FROM_PARENT }),
        partySlot('tenant_org_number', 'Hyresgäst – org.nr', 'lessee', 0, 'legalId', { required: true, help: FROM_PARENT }),
    ],
    compile: (input, ctx) => {
        const party = (nameKey, idKey) => [textVal(input, nameKey), t(' | org.nr '), textVal(input, idKey, 'short')];
        const rows = [
            ['Tilläggsavtal nr', [textVal(input, 'ta_number')]],
            ['Hyresavtal nr', [textVal(input, 'parent_contract_number')]],
            ['Fastighet', [textVal(input, 'property_designation')]],
            ['Lokalens adress', [textVal(input, 'property_address')]],
            ['Hyresvärd', party('landlord_name', 'landlord_org_number')],
            ['Hyresgäst', party('tenant_name', 'tenant_org_number')],
            shown(ctx, slotText(input.ta_date) != null) && ['Datum för tilläggsavtal', [textVal(input, 'ta_date')]],
        ];
        const grid = table([
            { key: 'label', width: 33 },
            { key: 'value', width: 67 },
        ], rows.filter((r) => Boolean(r)), { variant: 'details' });
        return [section('avtalskoppling', 'Avtalskoppling', [grid], { keepTogether: true })];
    },
};
const [A1, A2, A3] = ALTERNATIVE_OPTIONS;
const [UTB_A, UTB_B, UTB_C, UTB_D] = UTBETALNING_OPTIONS;
const [KOMP_I, KOMP_II] = KOMPENSATION_OPTIONS;
const inAlt = (alt, slot, extra) => ({
    ...slot,
    visibleWhen: extra ? { all: [whenOption('alternative', alt), extra] } : whenOption('alternative', alt),
});
export const hyresgastanpassning = {
    ...LEGAL,
    blockKey: `${K}.hyresgastanpassning`,
    version: 'v1',
    title: 'Hyresgästanpassning',
    summary: 'The chosen fit-out alternative (A1 grant, A2 landlord executes, A3 tenant at own cost) with its sub-clauses and choices.',
    slots: [
        selectSlot('alternative', 'Välj alternativ för hyresgästanpassning', ALTERNATIVE_OPTIONS, {
            required: true,
            default: A1.code,
            help: 'Mutually exclusive — endast det valda alternativet skrivs ut.',
        }),
        inAlt(A1, { key: 'a1_grant_amount', label: 'A1 — Investeringsbidrag (kr)', type: 'currency', min: 0, required: true }),
        inAlt(A1, { key: 'a1_completion_date', label: 'A1 — Färdigställandedatum', type: 'date', required: true }),
        inAlt(A1, selectSlot('a1_utbetalningsmodell', 'A1 — Utbetalningsmodell', UTBETALNING_OPTIONS, { required: true, default: UTB_A.code })),
        inAlt(A1, { key: 'a1_utb_a_start_pct', label: 'A1·A — Andel vid byggstart (%)', type: 'text', help: 't.ex. 50' }, whenOption('a1_utbetalningsmodell', UTB_A)),
        inAlt(A1, { key: 'a1_utb_a_rest_pct', label: 'A1·A — Resterande andel (%)', type: 'text', help: 't.ex. 50' }, whenOption('a1_utbetalningsmodell', UTB_A)),
        inAlt(A1, { key: 'a1_utb_b_part1_pct', label: 'A1·B — Andel vid byggstart (%)', type: 'text' }, whenOption('a1_utbetalningsmodell', UTB_B)),
        inAlt(A1, { key: 'a1_utb_b_part2_pct', label: 'A1·B — Andel vid milstolpe/delredovisning (%)', type: 'text' }, whenOption('a1_utbetalningsmodell', UTB_B)),
        inAlt(A1, { key: 'a1_utb_b_part3_pct', label: 'A1·B — Andel efter slutbesiktning (%)', type: 'text' }, whenOption('a1_utbetalningsmodell', UTB_B)),
        inAlt(A1, { key: 'a1_utb_c_days', label: 'A1·C — Senast antal dagar efter slutbesiktning', type: 'number', min: 0, integer: true }, whenOption('a1_utbetalningsmodell', UTB_C)),
        inAlt(A1, { key: 'a1_utb_d_max_payments', label: 'A1·D — Max antal utbetalningstillfällen', type: 'number', min: 1, integer: true }, whenOption('a1_utbetalningsmodell', UTB_D)),
        inAlt(A1, { key: 'a1_utb_d_processing_days', label: 'A1·D — Antal dagar för handläggning per faktura', type: 'number', min: 0, integer: true }, whenOption('a1_utbetalningsmodell', UTB_D)),
        inAlt(A2, {
            key: 'a2_work_description',
            label: 'A2 — Beskrivning av anpassningen',
            type: 'textarea',
            required: true,
            help: 'Beskriv anpassningen kortfattat eller komplettera med bilaga nedan.',
        }),
        inAlt(A2, { key: 'a2_work_description_annex', label: 'A2 — Bilaga med beskrivning (valfri)', type: 'attachment' }),
        inAlt(A2, { key: 'a2_completion_date', label: 'A2 — Färdigställandedatum', type: 'date', required: true }),
        inAlt(A2, { key: 'a2_notice_period', label: 'A2 — Förvarning före färdigställandedag', type: 'text', help: 't.ex. 30 dagar' }),
        inAlt(A2, { key: 'a2_inspection_window', label: 'A2 — Inflyttningsbesiktning inom', type: 'text', help: 't.ex. 14 dagar från tillträdesdagen' }),
        inAlt(A2, selectSlot('a2_kompensation', 'A2 — Kompensationsmodell', KOMPENSATION_OPTIONS, { required: true, default: KOMP_I.code })),
        inAlt(A2, { key: 'a2_komp_i_pct', label: 'A2·(i) — Pålägg (%)', type: 'text' }, whenOption('a2_kompensation', KOMP_I)),
        inAlt(A2, { key: 'a2_komp_i_days', label: 'A2·(i) — Senast antal dagar efter slutbesiktning', type: 'number', min: 0, integer: true }, whenOption('a2_kompensation', KOMP_I)),
        inAlt(A2, { key: 'a2_komp_ii_rate', label: 'A2·(ii) — Årlig ränta (%)', type: 'text' }, whenOption('a2_kompensation', KOMP_II)),
        inAlt(A2, { key: 'a2_komp_ii_monthly', label: 'A2·(ii) — Hyrestillägg (kr/månad)', type: 'currency', min: 0 }, whenOption('a2_kompensation', KOMP_II)),
        inAlt(A3, {
            key: 'a3_work_annex',
            label: 'A3 — Bilaga: ritningar / teknisk beskrivning',
            type: 'attachment',
            required: true,
            help: 'Välj eller skapa bilagan med ritningar och teknisk beskrivning.',
        }),
    ],
    compile: (input, ctx) => {
        const chosen = pick(ALTERNATIVE_OPTIONS, input.alternative);
        const content = [
            ...(shown(ctx, chosen === A1) ? alternativeA1(input, ctx) : []),
            ...(shown(ctx, chosen === A2) ? alternativeA2(input, ctx) : []),
            ...(shown(ctx, chosen === A3) ? alternativeA3(input, ctx) : []),
        ];
        return {
            nodes: [section('hyresgastanpassning', 'Hyresgästanpassning', content)],
            attachments: contextAttachments(ctx),
        };
    },
};
const staticSection = (name, ref, title, summary, text) => ({
    ...LEGAL,
    blockKey: `${K}.${name}`,
    version: 'v1',
    title,
    summary,
    slots: [],
    compile: () => [section(ref, title, [p(text)], { keepTogether: true })],
});
export const foretradesordning = staticSection('foretradesordning', 'foretradesordning', 'Företrädesordning', 'The supplement prevails over the lease on the matters it regulates; otherwise the lease applies in full.', 'Detta tilläggsavtal äger företräde framför hyresavtalet i de frågor som regleras häri. I övrigt gäller hyresavtalet och dess bilagor i sin helhet.');
export const skriftlighet = staticSection('skriftlighet', 'skriftlighet', 'Skriftlighetskrav', 'Changes to the supplement must be in writing.', 'Ändringar av och tillägg till detta tilläggsavtal ska vara skriftliga för att äga giltighet.');
export const DISPUTE_OPTIONS = [
    { code: 'general_court', value: 'domstol', label: 'Allmän domstol enligt svensk rätt', summary: 'Disputes are settled by general court under Swedish law.' },
    { code: 'per_annex', value: 'bilaga', label: 'Enligt särskild bilaga (t.ex. skiljedom)', summary: 'Disputes are settled per a separate annex (e.g. arbitration).' },
];
const [DOMSTOL, BILAGA] = DISPUTE_OPTIONS;
export const tvist = {
    ...LEGAL,
    blockKey: `${K}.tvist`,
    version: 'v1',
    title: 'Tvist',
    summary: 'Disputes go to general court under Swedish law, or follow a dispute-resolution annex.',
    slots: [
        selectSlot('dispute_mode', 'Tvistlösning', DISPUTE_OPTIONS, { required: true, default: DOMSTOL.code }),
        { key: 'dispute_annex', label: 'Bilaga – tvistlösningsordning', type: 'attachment', required: true, visibleWhen: whenOption('dispute_mode', BILAGA) },
    ],
    compile: (input, ctx) => {
        const annex = annexOrBlank(ctx, input.dispute_annex, 'dispute_annex');
        return {
            nodes: [
                section('tvist', 'Tvist', [
                    optionList([
                        option(ticked(ctx, optionChosen(DISPUTE_OPTIONS, input.dispute_mode, DOMSTOL)), 'Tvist avgörs av allmän domstol med tillämpning av svensk rätt, om inte tvingande regler i 12 kap. JB eller annan speciallagstiftning föranleder annat.', { code: DOMSTOL.code }),
                        option(ticked(ctx, optionChosen(DISPUTE_OPTIONS, input.dispute_mode, BILAGA)), [t('Tvist avgörs enligt den ordning parterna avtalat i '), ...annex, t('.')], { code: BILAGA.code }),
                    ], { select: 'single', sourceKey: 'dispute_mode' }),
                ], { keepTogether: true }),
            ],
            attachments: contextAttachments(ctx),
        };
    },
};
export const bilagor = {
    blockKey: `${K}.bilagor`,
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Bilagor till detta tilläggsavtal',
    summary: 'The supplement’s bilaga list, generated from the attachments the contract actually carries.',
    slots: [
        {
            key: 'extra_attachments',
            label: 'Bilagor till tilläggsavtalet',
            type: 'attachment',
            repeats: true,
            help: 'Skapa och namnge de bilagor som hör till tilläggsavtalet. Exempel: ' +
                'teknisk beskrivning / kravspecifikation för hyresgästanpassningen, ' +
                'ritningar, tidsplan. De bilagor du lägger till listas automatiskt ' +
                'i avtalets bilageförteckning.',
        },
    ],
    compile: (_input, ctx) => ({
        nodes: [section('bilagor', 'Bilagor till detta tilläggsavtal', attachmentListNodes(ctx))],
        attachments: ctx.mode === 'blank' ? [] : contextAttachments(ctx),
    }),
};
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const underskrifter = signingBlockDefinition({ blockKey: `${K}.underskrifter` });
export const TILLAGGSAVTAL_HYRESGASTANPASSNING_BLOCKS = [
    avtalskoppling,
    hyresgastanpassning,
    foretradesordning,
    skriftlighet,
    tvist,
    underskrifter,
    bilagor,
    customSections,
];
//# sourceMappingURL=blocks.js.map